import {
  Link,
  Sidebar,
  SidebarDivider,
  SidebarGroup,
  SidebarItem,
  SidebarPage,
  SidebarScrollWrapper,
  SidebarSpace,
  sidebarConfig,
  useSidebarOpenState,
} from '@backstage/core-components';
import { featureFlagsApiRef, useApi } from '@backstage/core-plugin-api';
import { MyGroupsSidebarItem } from '@backstage/plugin-org';
import { usePermission } from '@backstage/plugin-permission-react';
import { SidebarSearchModal } from '@backstage/plugin-search';
import {
  Settings as SidebarSettings,
  UserSettingsSignInAvatar,
} from '@backstage/plugin-user-settings';
import { makeStyles } from '@material-ui/core';
import CreateComponentIcon from '@material-ui/icons/AddCircleOutline';
import BatchIcon from '@material-ui/icons/PlaylistAddCheck';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import ExtensionIcon from '@material-ui/icons/Extension';
import HomeIcon from '@material-ui/icons/Home';
import LibraryBooks from '@material-ui/icons/LibraryBooks';
import MenuIcon from '@material-ui/icons/Menu';
import GroupIcon from '@material-ui/icons/People';
import SearchIcon from '@material-ui/icons/Search';
import IncidentIcon from '@material-ui/icons/Warning';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import ViewListIcon from '@material-ui/icons/ViewList';
import TimelineIcon from '@material-ui/icons/Timeline';
import { PropsWithChildren } from 'react';
import LogoFull from './LogoFull';
import LogoIcon from './LogoIcon';
import { JIRA_BOARDS_TAB_FEATURE_FLAG, ORGANIZATION_TAB_FEATURE_FLAG, UPTIME_TAB_FEATURE_FLAG } from '../../featureFlags/uptime';
import { batchEditorUsePermission } from '../../permissions/batchEditor';

const useSidebarLogoStyles = makeStyles({
  root: {
    width: sidebarConfig.drawerWidthClosed,
    height: 3 * sidebarConfig.logoHeight,
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    marginBottom: -14,
  },
  link: {
    width: sidebarConfig.drawerWidthClosed,
    marginLeft: 24,
  },
});

const SidebarLogo = () => {
  const classes = useSidebarLogoStyles();
  const { isOpen } = useSidebarOpenState();

  return (
    <div className={classes.root}>
      <Link to="/" underline="none" className={classes.link} aria-label="Home">
        {isOpen ? <LogoFull /> : <LogoIcon />}
      </Link>
    </div>
  );
};

const BatchEditorSidebarItem = () => {
  const { allowed } = usePermission({ permission: batchEditorUsePermission });
  if (!allowed) {
    return null;
  }
  return <SidebarItem icon={BatchIcon} to="/batch-editor" text="Batch Editor" />;
};

const UptimeSidebarItem = () => {
  const featureFlags = useApi(featureFlagsApiRef);
  if (!featureFlags.isActive(UPTIME_TAB_FEATURE_FLAG)) {
    return null;
  }
  return <SidebarItem icon={TimelineIcon} to="/uptime" text="Uptime" />;
};

const JiraBoardsSidebarItem = () => {
  const featureFlags = useApi(featureFlagsApiRef);
  if (!featureFlags.isActive(JIRA_BOARDS_TAB_FEATURE_FLAG)) {
    return null;
  }
  return <SidebarItem icon={ViewListIcon} to="/jira-boards" text="Jira Boards" />;
};

const OrganizationSidebarItem = () => {
  const featureFlags = useApi(featureFlagsApiRef);
  if (!featureFlags.isActive(ORGANIZATION_TAB_FEATURE_FLAG)) {
    return null;
  }
  return <SidebarItem icon={AccountTreeIcon} to="/organization" text="Organization" />;
};

export const Root = ({ children }: PropsWithChildren<{}>) => {
  return (
    <SidebarPage>
      <Sidebar>
        <SidebarLogo />
        <SidebarGroup label="Search" icon={<SearchIcon />} to="/search">
          <SidebarSearchModal />
        </SidebarGroup>
        <SidebarDivider />
        <SidebarGroup label="Menu" icon={<MenuIcon />}>
          {/* Global nav, not org-specific */}
          <SidebarItem icon={HomeIcon} to="catalog" text="Home" />
          <MyGroupsSidebarItem singularTitle="My Group" pluralTitle="My Groups" icon={GroupIcon} />
          <SidebarItem icon={ExtensionIcon} to="api-docs" text="APIs" />
          <SidebarItem icon={LibraryBooks} to="docs" text="Docs" />
          <SidebarItem icon={CreateComponentIcon} to="create" text="Create..." />
          <SidebarItem icon={IncidentIcon} to="/incidents" text="Incidents" />
          <BatchEditorSidebarItem />
          {/* End global nav */}
          <SidebarDivider />
          <SidebarScrollWrapper>
            {/* Items in this group will be scrollable if they run out of space */}
            <SidebarItem icon={DoneAllIcon} to="soundcheck" text="Health Checks" />
            <JiraBoardsSidebarItem />
            <OrganizationSidebarItem />
            <UptimeSidebarItem />
          </SidebarScrollWrapper>
        </SidebarGroup>
        <SidebarSpace />
        <SidebarDivider />
        <SidebarGroup label="Settings" icon={<UserSettingsSignInAvatar />} to="/settings">
          <SidebarSettings />
        </SidebarGroup>
      </Sidebar>
      {children}
    </SidebarPage>
  );
};
